import React, { useState, useEffect } from 'react';
import DashboardLayout from '../components/DashboardLayout';
import ConfirmModal from '../components/ConfirmModal';
import {
  getTariffs,
  createTariff,
  updateTariff,
  deleteTariff,
  Tariff,
  getAmbulanceTypes,
} from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

interface AmbulanceTypeOption {
  _id: string;
  name: string;
  displayName: string;
}

interface TariffManagementProps {
  skipLayout?: boolean;
}

const TariffManagement: React.FC<TariffManagementProps> = ({ skipLayout = false }) => {
  const { hasPermission } = useAuth();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [ambulanceTypes, setAmbulanceTypes] = useState<AmbulanceTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [tariffToDelete, setTariffToDelete] = useState<{ id: string; name: string } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [editingTariff, setEditingTariff] = useState<Tariff | null>(null);
  const [formData, setFormData] = useState({
    ambulanceTypeId: '',
    rangeType: 'km_range' as 'km_range' | 'per_km' | 'detention',
    minKm: '',
    maxKm: '',
    charge: '',
    description: '',
  });
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(100);
  const [totalItems, setTotalItems] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const [filterAmbulanceTypeId, setFilterAmbulanceTypeId] = useState('');

  // Close drawer with Escape key
  useEffect(() => {
    if (!showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showModal]);

  useEffect(() => {
    fetchAmbulanceTypes();
    fetchTariffs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchTariffs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAmbulanceTypeId]);

  const fetchAmbulanceTypes = async () => {
    try {
      const response = await getAmbulanceTypes(1, 200);
      const types = (response.data.data || []).map((t: any) => ({
        _id: t._id,
        name: t.metadata.name,
        displayName: t.metadata.displayName || t.metadata.name,
      }));
      setAmbulanceTypes(types);
    } catch (err) {
      console.error('Failed to load ambulance types:', err);
    }
  };

  const fetchTariffs = async (page: number) => {
    try {
      setLoading(true);
      const response = await getTariffs(
        page,
        pageSize,
        filterAmbulanceTypeId || undefined
      );
      setTariffs(response.data.data || []);
      const pagination = response.data.pagination || {};
      setCurrentPage(pagination.currentPage || page);
      setTotalItems(pagination.totalItems || 0);
      setHasNextPage(pagination.hasNextPage || false);
      setHasPreviousPage(pagination.hasPreviousPage || false);
    } catch (err: any) {
      setError('Failed to load tariffs');
      console.error('Failed to load tariffs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tariff?: Tariff) => {
    if (tariff) {
      setEditingTariff(tariff);
      setFormData({
        ambulanceTypeId: tariff.spec.ambulanceTypeId,
        rangeType: tariff.spec.rangeType,
        minKm: tariff.spec.minKm !== undefined ? String(tariff.spec.minKm) : '',
        maxKm: tariff.spec.maxKm !== undefined ? String(tariff.spec.maxKm) : '',
        charge: String(tariff.spec.charge),
        description: tariff.spec.description || '',
      });
    } else {
      setEditingTariff(null);
      setFormData({
        ambulanceTypeId: filterAmbulanceTypeId || '',
        rangeType: 'km_range',
        minKm: '',
        maxKm: '',
        charge: '',
        description: '',
      });
    }
    setShowModal(true);
    setError('');
    setValidationErrors({});
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setValidationErrors({});
    setSubmitting(true);

    // Validate
    const errors: Record<string, string> = {};
    if (!formData.ambulanceTypeId) errors.ambulanceTypeId = 'Ambulance type is required';
    if (!formData.charge || isNaN(Number(formData.charge)) || Number(formData.charge) < 0) {
      errors.charge = 'Charge must be a valid non-negative number';
    }
    if (formData.rangeType === 'km_range') {
      if (formData.minKm === '' || isNaN(Number(formData.minKm))) errors.minKm = 'Min Km is required';
      if (formData.maxKm === '' || isNaN(Number(formData.maxKm))) errors.maxKm = 'Max Km is required';
      if (!errors.minKm && !errors.maxKm && Number(formData.minKm) > Number(formData.maxKm)) {
        errors.minKm = 'Min Km cannot be greater than max Km';
      }
    }
    if (formData.rangeType === 'per_km') {
      if (formData.minKm === '' || isNaN(Number(formData.minKm))) errors.minKm = 'Threshold Km is required';
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      setError('Please fix the highlighted errors.');
      setSubmitting(false);
      return;
    }

    try {
      const data = {
        spec: {
          ambulanceTypeId: formData.ambulanceTypeId,
          rangeType: formData.rangeType,
          minKm: formData.rangeType !== 'detention' ? Number(formData.minKm) : undefined,
          maxKm: formData.rangeType === 'km_range' ? Number(formData.maxKm) : undefined,
          charge: Number(formData.charge),
          description: formData.description.trim() || undefined,
        },
      };

      if (editingTariff) {
        await updateTariff(editingTariff._id, data);
      } else {
        await createTariff(data);
      }

      setShowModal(false);
      fetchTariffs(currentPage);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setTariffToDelete({ id, name });
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!tariffToDelete) return;
    try {
      await deleteTariff(tariffToDelete.id);
      const nextPage = currentPage > 1 && tariffs.length === 1 ? currentPage - 1 : currentPage;
      fetchTariffs(nextPage);
      setShowDeleteConfirm(false);
      setTariffToDelete(null);
    } catch (err: any) {
      setErrorMessage(err.response?.data?.message || 'Failed to delete tariff.');
      setShowDeleteConfirm(false);
      setTariffToDelete(null);
      setShowErrorModal(true);
    }
  };

  const getRangeTypeLabel = (rangeType: string) => {
    switch (rangeType) {
      case 'km_range': return 'Km Range';
      case 'per_km': return 'Per Km';
      case 'detention': return 'Detention';
      default: return rangeType;
    }
  };

  const getRangeDisplay = (tariff: Tariff) => {
    if (tariff.spec.rangeType === 'km_range') {
      return `${tariff.spec.minKm} - ${tariff.spec.maxKm} Kms`;
    } else if (tariff.spec.rangeType === 'per_km') {
      return `Above ${tariff.spec.minKm} Kms`;
    } else if (tariff.spec.rangeType === 'detention') {
      return 'Per 30 Mins';
    }
    return '-';
  };

  // Group tariffs by ambulance type for display
  const groupedTariffs: Record<string, { typeName: string; tariffs: Tariff[] }> = {};
  tariffs.forEach((t) => {
    const key = t.spec.ambulanceTypeId;
    if (!groupedTariffs[key]) {
      groupedTariffs[key] = {
        typeName: t.spec.ambulanceTypeName || 'Unknown',
        tariffs: [],
      };
    }
    groupedTariffs[key].tariffs.push(t);
  });

  const content = (
    <>
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <select
                value={filterAmbulanceTypeId}
                onChange={(e) => setFilterAmbulanceTypeId(e.target.value)}
                className="search-input"
                style={{ minWidth: '200px' }}
              >
                <option value="">All Ambulance Types</option>
                {ambulanceTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.displayName}
                  </option>
                ))}
              </select>
              <button
                onClick={() => fetchTariffs(currentPage)}
                disabled={loading}
                className="refresh-button"
                title="Refresh"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"></polyline>
                  <polyline points="1 20 1 14 7 14"></polyline>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
                </svg>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            {hasPermission('tariff.create') && (
              <button className="btn-add" onClick={() => handleOpenModal()}>
                + Add Tariff
              </button>
            )}
          </div>
        </div>

        {error && <div className="alert alert-danger" style={{ margin: '16px' }}>{error}</div>}

        {loading ? (
          <div className="loading">Loading tariffs...</div>
        ) : tariffs.length === 0 ? (
          <div className="empty-state">
            <h3>No tariffs found</h3>
            {hasPermission('tariff.create') && <p>Click "+ Add Tariff" to create one.</p>}
          </div>
        ) : (
          <div style={{ position: 'relative' }}>
            <div className="table-container" style={{ height: 'calc(100vh - 450px)', overflow: 'auto' }}>
              <table className="bookings-table">
                <thead style={{ position: 'sticky', top: 0, zIndex: 10 }}>
                  <tr>
                    {/* <th>Ambulance Type</th> */}
                    <th>Range Type</th>
                    <th>Range</th>
                    <th style={{ textAlign: 'right' }}>Charge (&#8377;)</th>
                    <th>Description</th>
                    {(hasPermission('tariff.update') || hasPermission('tariff.delete')) && (
                      <th>Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(groupedTariffs).map(([typeId, group]) => (
                    <React.Fragment key={typeId}>
                      <tr>
                        <td colSpan={hasPermission('tariff.update') || hasPermission('tariff.delete') ? 6 : 5}
                          style={{
                            padding: '10px 16px',
                            fontWeight: 700,
                            fontSize: '15px',
                            backgroundColor: isDark ? '#1e293b' : '#f1f5f9',
                            color: isDark ? '#e2e8f0' : '#1e293b',
                            borderBottom: 'none',
                          }}
                        >
                          {group.typeName}
                        </td>
                      </tr>
                      {group.tariffs.map((tariff) => (
                        <tr key={tariff._id}>
                          {/* <td>
                            <span style={{ color: isDark ? '#94a3b8' : '#6b7280', fontSize: '13px' }}>
                              {group.typeName}
                            </span>
                          </td> */}
                          <td>
                            <span style={{
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 500,
                              backgroundColor: tariff.spec.rangeType === 'km_range'
                                ? (isDark ? '#1e3a5f' : '#dbeafe')
                                : tariff.spec.rangeType === 'per_km'
                                  ? (isDark ? '#3b2f1e' : '#fef3c7')
                                  : (isDark ? '#2d1b3d' : '#ede9fe'),
                              color: tariff.spec.rangeType === 'km_range'
                                ? (isDark ? '#93c5fd' : '#1d4ed8')
                                : tariff.spec.rangeType === 'per_km'
                                  ? (isDark ? '#fcd34d' : '#b45309')
                                  : (isDark ? '#c4b5fd' : '#6d28d9'),
                            }}>
                              {getRangeTypeLabel(tariff.spec.rangeType)}
                            </span>
                          </td>
                          <td style={{ fontWeight: 500 }}>
                            {getRangeDisplay(tariff)}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 600, fontSize: '15px' }}>
                            {tariff.spec.rangeType === 'per_km'
                              ? `${tariff.spec.charge} / Km`
                              : tariff.spec.rangeType === 'detention'
                                ? `${tariff.spec.charge} / 30 Mins`
                                : tariff.spec.charge.toLocaleString()}
                          </td>
                          <td style={{ color: isDark ? '#94a3b8' : '#6b7280', fontSize: '13px' }}>
                            {tariff.spec.description || '—'}
                          </td>
                          {(hasPermission('tariff.update') || hasPermission('tariff.delete')) && (
                            <td>
                              <div className="action-buttons">
                                {hasPermission('tariff.update') && (
                                  <button className="btn-icon btn-edit" onClick={() => handleOpenModal(tariff)}>
                                    Edit
                                  </button>
                                )}
                                {hasPermission('tariff.delete') && (
                                  <button
                                    className="btn-icon btn-delete"
                                    onClick={() => handleDelete(tariff._id, tariff.metadata.name)}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalItems > pageSize && (
          <div className="pagination">
            <button
              className="btn-page"
              onClick={() => fetchTariffs(currentPage - 1)}
              disabled={!hasPreviousPage}
            >
              Previous
            </button>
            <span className="page-info">
              Page {currentPage} of {Math.ceil(totalItems / pageSize)}
            </span>
            <button
              className="btn-page"
              onClick={() => fetchTariffs(currentPage + 1)}
              disabled={!hasNextPage}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Drawer */}
      {showModal && (
        <div className="drawer-overlay" onClick={() => setShowModal(false)}>
          <div className="drawer" onClick={(e) => e.stopPropagation()}>
            <div className="drawer-header">
              <h3 className="drawer-title">{editingTariff ? 'Edit Tariff' : 'Add Tariff'}</h3>
              <button className="drawer-close" onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmit} className="drawer-form">
              <div className="drawer-body">
                {error && (
                  <div className="alert alert-danger" style={{ marginBottom: '16px' }}>
                    {error}
                  </div>
                )}

                {/* Ambulance Type */}
                <div className="form-group">
                  <label>
                    Ambulance Type <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.ambulanceTypeId}
                    onChange={(e) => setFormData({ ...formData, ambulanceTypeId: e.target.value })}
                    disabled={!!editingTariff}
                  >
                    <option value="">Select ambulance type</option>
                    {ambulanceTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.displayName}
                      </option>
                    ))}
                  </select>
                  {validationErrors.ambulanceTypeId && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.ambulanceTypeId}
                    </small>
                  )}
                </div>

                {/* Range Type */}
                <div className="form-group">
                  <label>
                    Charge Type <span className="required-star">*</span>
                  </label>
                  <select
                    value={formData.rangeType}
                    onChange={(e) => setFormData({ ...formData, rangeType: e.target.value as any })}
                  >
                    <option value="km_range">Km Range (flat charge for range)</option>
                    <option value="per_km">Per Km (above threshold)</option>
                    <option value="detention">Detention (per 30 mins)</option>
                  </select>
                </div>

                {/* Min KM */}
                {formData.rangeType !== 'detention' && (
                  <div className="form-group">
                    <label>
                      {formData.rangeType === 'per_km' ? 'Above Km (threshold)' : 'Min Km'} <span className="required-star">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.minKm}
                      onChange={(e) => setFormData({ ...formData, minKm: e.target.value })}
                      placeholder={formData.rangeType === 'per_km' ? 'e.g. 110' : 'e.g. 0'}
                    />
                    {validationErrors.minKm && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.minKm}
                      </small>
                    )}
                  </div>
                )}

                {/* Max KM */}
                {formData.rangeType === 'km_range' && (
                  <div className="form-group">
                    <label>
                      Max Km <span className="required-star">*</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={formData.maxKm}
                      onChange={(e) => setFormData({ ...formData, maxKm: e.target.value })}
                      placeholder="e.g. 5"
                    />
                    {validationErrors.maxKm && (
                      <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                        {validationErrors.maxKm}
                      </small>
                    )}
                  </div>
                )}

                {/* Charge */}
                <div className="form-group">
                  <label>
                    Charge (&#8377;) <span className="required-star">*</span>
                    {formData.rangeType === 'per_km' && (
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '12px' }}> (per Km)</span>
                    )}
                    {formData.rangeType === 'detention' && (
                      <span style={{ fontWeight: 400, color: '#6b7280', fontSize: '12px' }}> (per 30 Mins)</span>
                    )}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={formData.charge}
                    onChange={(e) => setFormData({ ...formData, charge: e.target.value })}
                    placeholder="e.g. 300"
                  />
                  {validationErrors.charge && (
                    <small style={{ color: '#e74c3c', fontSize: '14px', marginTop: '4px', display: 'block' }}>
                      {validationErrors.charge}
                    </small>
                  )}
                </div>

                {/* Description */}
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g. BLS Ambulance Charge"
                  />
                </div>
              </div>

              <div className="drawer-footer">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit" disabled={submitting}>
                  {submitting ? 'Saving...' : editingTariff ? 'Update Tariff' : 'Create Tariff'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmModal
        isOpen={showDeleteConfirm}
        title="Delete Tariff"
        message={`Are you sure you want to delete "${tariffToDelete?.name}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setTariffToDelete(null);
        }}
        type="danger"
      />

      {/* Error Modal */}
      <ConfirmModal
        isOpen={showErrorModal}
        title="Error"
        message={errorMessage}
        confirmText="OK"
        cancelText=""
        onConfirm={() => setShowErrorModal(false)}
        onCancel={() => setShowErrorModal(false)}
        type="danger"
      />
    </>
  );

  if (skipLayout) {
    return content;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default TariffManagement;
